import React, { useEffect, useState, useMemo } from "react";

import Layout from 'components/Layout/Layout';
import NotchCalendar from 'components/NotchCalendar'
import Details from "components/Notches/Details";
import Form from "components/Notches/Form";

import OmniButton from "./help/OmniButton";
import ListCard from "./help/ListCard";

import { useRefreshOnFocus } from 'hooks/useRefreshOnFocus';

import { getNotchTypes } from 'services/notches';
import { groupByGroups } from 'services/groups';

import { useLocalStorage } from 'utils/storage';
import { useSettings } from 'utils/settings';
import axiosInstance from 'utils/axiosInstance';
import echo from 'utils/echo';

import "./styles.css";

const Notches = () => {
    const today = new Date().toLocaleDateString("en-CA");
    const [selectedDate, setSelectedDate] = useLocalStorage('selectedDate', today);
    const [onDateSet, setOnDateSet] = useLocalStorage('onDateSet', today);

    // open today on focus
    useRefreshOnFocus(() => {
        const todayRefreshed = new Date().toLocaleDateString("en-CA");

        if (onDateSet !== todayRefreshed) {
            setOnDateSet(todayRefreshed);
            setSelectedDate(todayRefreshed);
        }
    });

    const [daysWithNotches, setDaysWithNotches] = useLocalStorage('daysWithNotches', []);

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [currentState, setCurrentState] = useState('dashboard');
    const [user, setUser] = useLocalStorage('user');

    const [selectedNotch, setSelectedNotch] = useState(null);
    const [notchToEdit, setNotchToEdit] = useState(null);

    const [notches, setNotches] = useLocalStorage('notches', []);
    const [favorites, setFavorites] = useLocalStorage('favorites', []);
    const [tags, setTags] = useLocalStorage('tags', []);

    const [settings, setSettings] = useSettings();


    function settingChangeHandler(section, settingId) {
        setSettings(prev => {
            const currentValue = prev[section][settingId];
            const value = settingId === 'sort'
                ? (currentValue === 'asc' ? 'desc' : 'asc')
                : !currentValue;

            return {
                ...prev,
                [section]: {
                    ...prev[section],
                    [settingId]: value
                }
            };
        });
    }

    const [notchTypes, setNotchTypes] = useState(getNotchTypes());

    const fetchItems = async ({selectedDate}) => {
        try {
            const response = await axiosInstance.get(`/api/notches?date=${selectedDate}`);

            return response.data;
        } catch (error) {
            return [];
        }
    };

    // TODO: change to general dictionary
    useEffect(() => {
        fetchItems({ selectedDate }).then((data) => {
            setNotches(data.data.notches);
            setDaysWithNotches(data.data.days_with_notches);
        })
    }, [selectedDate]);

    // #region Focus
    useRefreshOnFocus(() => {
        fetchItems({ selectedDate }).then((data) => {
            setNotches(data.data.notches);
            setDaysWithNotches(data.data.days_with_notches);
        })
    });
    // #endregion

    // #region Sockets
    useEffect(() => {
        echo.connector.pusher.connection.bind('connected', () => {
            const channel = echo.private(`user.${user.uid}.notches`)

            channel.listen('.NotchEvent', (e) => {
                if (selectedDate != e.data.date) {
                    return;
                }

                if (e.action === 'created') {
                    setNotches(prev => [...prev, e.data]);
                }
                if (e.action === 'updated') {
                    setNotches(prev => prev.map(n => n.uid === e.data.uid ? e.data : n));
                }
                if (e.action === 'deleted') {
                    setNotches(prev => prev.filter(n => n.uid !== e.data.uid));
                }
            });

            return () => echo.leaveChannel('notches');
        });
    }, [selectedDate]);
    // #endregion

    const handleAddItem = async (form) => {
        try {
            let response;

            if (form.uid) {
                response = await axiosInstance.post(`/api/notches/${form.uid}`, form);
            } else {
                response = await axiosInstance.post("/api/notches", form);
            }

            const itemInResponse = response.data.data;

            setNotches((currentItems) => {
                const isEditing = currentItems.some(n => n.uid === itemInResponse.uid);

                if (isEditing) {
                    return currentItems.map(item =>
                        item.uid === itemInResponse.uid ? itemInResponse : item
                    );
                } else {
                    const alreadyExists = daysWithNotches.includes(itemInResponse.date);

                    if (!alreadyExists) {
                        setDaysWithNotches(prev => [...prev, itemInResponse.date]);
                    }

                    return [...currentItems, itemInResponse];
                }
            });

            setCurrentState('dashboard');
        } catch (error) {
            console.error('Failed to save notch:', error);
        }
    };

    const handleAddFromFavorite = async (item) => {
        try {
            let response = await axiosInstance.post("/api/notches/from-favorite", {
                favorite_uid: item.uid,
                date: selectedDate
            });

            const itemInResponse = response.data.data;

            setNotches((currentItems) => {
                const alreadyExists = daysWithNotches.includes(itemInResponse.date);

                if (!alreadyExists) {
                    setDaysWithNotches(prev => [...prev, itemInResponse.date]);
                }

                return [...currentItems, itemInResponse];
            });
        } catch (error) {
            console.error('Failed to save notch:', error);
        }

        setCurrentState('dashboard');
    }

    // #region Notch Details
    const handleOpenNotchDetails = async (item) => {
        setSelectedNotch(item);

        setCurrentState('details');
    }

    const handleEditNotch = (item) => {
        setNotchToEdit(item);

        setCurrentState('edit');
    }

    const handleDeleteNotch = async (uid) => {
        try {
            await axiosInstance.delete(`/api/notches/${uid}`);

            handleCloseNotchDetails(uid);
        } catch (error) {
            console.error('Failed to delete notch:', error);
        }
    };

    const handleCloseNotchDetails = (uid) => {
        // if uid is not null -> item was deleted
        if(uid !== null){
            setNotches((notches) => notches.filter(n => n.uid !== uid));

            setNotches((currentNotches) => {
                const remainingNotchesForDate = currentNotches.filter(n => n.date === selectedDate);

                if (remainingNotchesForDate.length === 0) {
                    setDaysWithNotches((days) => days.filter(date => date !== selectedDate));
                }

                return currentNotches;
            });
        }

        setCurrentState('dashboard')
    }

    const handleFormCancel = () => {
        setNotchToEdit(null);
        setCurrentState('dashboard');
    }
    // #endregion

    const sortedNotches = useMemo(() => {
        const order = settings.notches.sort === 'desc' ? 1 : -1;
        return [...notches].toSorted((a, b) =>
            order * (new Date(a.created_at) - new Date(b.created_at))
        );
    }, [notches, settings.notches.sort]);

    const renderCurrentView = () => {
        switch (currentState) {
            case "dashboard":
                const notchesListSettings = {
                    'section': 'notches',
                    'items': {
                        'showTime': {
                            'label': 'Show time',
                            'type': 'switch',
                            // icon: <JournalBookmark size={24} />,
                            'checked': settings.notches.showTime
                        },
                        'showTags': {
                            'label': 'Show tags',
                            'type': 'switch',
                            // icon: <JournalBookmark size={24} />,
                            'checked': settings.notches.showTags
                        },
                        'divider': {
                            'type': 'divider'
                        },
                        'sort': {
                            'label': 'Date added',
                            'type': 'sort_switch',
                            'value': settings.notches.sort
                        }
                    }
                };

                return (
                    <ListCard 
                        header={selectedDate}
                        items={sortedNotches}
                        settings={notchesListSettings}
                        selectedDate={selectedDate}
                        onSettingChange={settingChangeHandler}
                        onClickHandler={handleOpenNotchDetails}
                        onEditHandler={handleEditNotch}
                        onDeleteHandler={handleDeleteNotch}
                    />
                );
            case "details":
                return (
                    <Details
                        item={selectedNotch}
                        onEditHandler={handleEditNotch}
                        onDeleteHandler={handleDeleteNotch}
                        onCloseHandler={handleCloseNotchDetails}
                    />
                );
            case "add":
            case "edit":
                return (
                    <Form
                        type='notch'
                        selectedDate={selectedDate}
                        notchTypes={notchTypes}
                        addItemHandler={handleAddItem}
                        onCancelHandler={handleFormCancel}
                        editingNotch={notchToEdit}
                    />
                );
            case "add_from_favorites":
                const favoritesListSettings = {
                    'section': 'favorites',
                    'items': {
                        'displayGroups': {
                            'label': 'Display groups',
                            'type': 'switch',
                            'checked': settings.favorites.displayGroups
                        },
                    }
                };

                const listType = favoritesListSettings.items.displayGroups.checked ? 'grouped' : 'default';
                
                const getModifiedItems = (listType) => {
                    switch (listType) {
                        case 'grouped':
                            return groupByGroups(favorites);
                        default:
                            return favorites;
                    }
                }

                return  (
                    <ListCard
                        header="Favorites"
                        items={getModifiedItems(listType)}
                        listType={listType}
                        settings={favoritesListSettings}
                        onSettingChange={settingChangeHandler}
                        onClickHandler={handleAddFromFavorite}
                        onCancelHandler={() => setCurrentState('dashboard')}
                        omniContainer = {!isMobile}
                    />
                )
            default:
                return <div>Unknown state</div>;
        }
    };


    return (
        <Layout 
            title="Notches"
            omniButton={currentState == 'dashboard' && (
                <OmniButton
                    favorites={favorites}
                    setCurrentState={setCurrentState}
                    handleAddFromFavorite={handleAddFromFavorite}
                />
            )}
        >
            <div className="row">

                {/* Sidebar */}
                {!isMobile || currentState == 'dashboard' ? (
                    <div className="col-md-4">

                        <div className="card align-items-center">
                            <NotchCalendar
                                selectedDate={selectedDate}
                                setSelectedDate={setSelectedDate}
                                setNotches={setNotches}
                                setDaysWithNotches={setDaysWithNotches}
                                daysWithNotches={daysWithNotches}
                                onDateChangeAction={() => setCurrentState('dashboard')}
                            />
                        </div>

                        {/* <div className="card">
                        <div className="card-body">
                            <ul className="list-group">
                                <li className="list-group-item">custom item</li>
                            </ul>
                        </div>
                    </div> */}


                    </div>
                ) : null}

                <div className="col-md-1">

                </div>

                <div className="col-md-7">
                    {renderCurrentView()}
                </div>
            </div>
        </Layout>
    );
};

export default Notches;
